import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Info, Wallet, Gauge, Timer, FileText, Play } from "lucide-react";
import api, { inr, formatApiError } from "./api";
import { PageHeader, inputCls, selectCls } from "./components";
import { EASE } from "@/components/Motion";

const PLATFORMS = ["Instagram", "YouTube", "Facebook", "TikTok", "Telegram", "X", "Other"];
const PLATFORM_LABELS = { X: "X / Twitter" };

export default function NewOrderPage() {
  const { user, refreshUser } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [platform, setPlatform] = useState("Instagram");
  const [category, setCategory] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [link, setLink] = useState("");
  const [quantity, setQuantity] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/services").then(({ data }) => {
      setServices(data);
      const pre = searchParams.get("service");
      if (pre) {
        const svc = data.find((s) => s.sid === pre);
        if (svc) {
          setPlatform(svc.platform);
          setCategory(svc.category);
          setServiceId(svc.sid);
        }
      }
    });
  }, [searchParams]);

  const categories = useMemo(
    () => [...new Set(services.filter((s) => s.platform === platform).map((s) => s.category))],
    [services, platform]
  );
  const platformServices = useMemo(
    () => services.filter((s) => s.platform === platform && (!category || s.category === category)),
    [services, platform, category]
  );
  const selected = services.find((s) => s.sid === serviceId) || null;
  const qty = parseInt(quantity, 10) || 0;
  const charge = selected && qty ? (selected.rate * qty) / 1000 : 0;
  const insufficient = selected && charge > (user?.balance ?? 0);

  const placeOrder = async () => {
    if (!selected || !link.trim() || !qty) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { service_id: selected.sid, link: link.trim(), quantity: qty });
      toast.success(`Order ${data.order_id} placed`, { description: `${selected.name} · ${qty.toLocaleString("en-IN")} units · ${inr(data.charge)}` });
      refreshUser().catch(() => {});
      navigate("/panel/orders");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div data-testid="new-order-page">
      <PageHeader title="New Order" sub="Pick a platform, lock a service, drop the link. We handle the rest." />

      <div className="grid gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="space-y-6 lg:col-span-7"
        >
          <div className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">01 — Platform</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  onClick={() => { setPlatform(p); setCategory(""); setServiceId(""); }}
                  data-testid={`neworder-platform-${p.toLowerCase()}`}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300 ${
                    platform === p
                      ? "border-gold bg-gold/10 text-goldlight"
                      : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
                  }`}
                >
                  {PLATFORM_LABELS[p] || p}
                </button>
              ))}
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">02 — Category</label>
                <select
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setServiceId(""); }}
                  className={`${selectCls} mt-3`}
                  data-testid="neworder-category-select"
                >
                  <option value="">All categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">03 — Service</label>
                <select
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  className={`${selectCls} mt-3`}
                  data-testid="neworder-service-select"
                >
                  <option value="">Choose a service</option>
                  {platformServices.map((s) => (
                    <option key={s.sid} value={s.sid}>
                      {s.sid} — {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-8">
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">04 — Link</label>
              <input
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://instagram.com/yourpost"
                className={`${inputCls} mt-3`}
                data-testid="neworder-link-input"
              />
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">05 — Quantity</label>
                {selected && (
                  <span className="font-mono text-[10px] tracking-wider text-neutral-500">
                    MIN {selected.min.toLocaleString("en-IN")} · MAX {selected.max.toLocaleString("en-IN")}
                  </span>
                )}
              </div>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min={selected?.min}
                max={selected?.max}
                placeholder={selected ? `${selected.min.toLocaleString("en-IN")} — ${selected.max.toLocaleString("en-IN")}` : "Select a service first"}
                className={`${inputCls} mt-3`}
                data-testid="neworder-quantity-input"
              />
              {selected && qty > 0 && (qty < selected.min || qty > selected.max) && (
                <p className="mt-2 text-xs text-amber-300" data-testid="neworder-quantity-warning">
                  Quantity must be between {selected.min.toLocaleString("en-IN")} and {selected.max.toLocaleString("en-IN")}.
                </p>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}
          className="space-y-6 lg:col-span-5"
        >
          <div className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="neworder-service-details">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Service details</div>
            {selected ? (
              <div className="mt-5 space-y-5">
                <div>
                  <div className="font-semibold text-neutral-100">{selected.name}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-gold/70">ID {selected.sid} · {selected.platform.toUpperCase()}</div>
                </div>
                <p className="flex gap-3 text-sm leading-relaxed text-neutral-400">
                  <FileText size={15} className="mt-0.5 shrink-0 text-gold/70" />
                  {selected.description}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                    <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] text-neutral-500"><Gauge size={11} /> RATE / 1K</div>
                    <div className="mt-1.5 font-semibold text-goldlight">{inr(selected.rate)}</div>
                  </div>
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                    <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] text-neutral-500"><Timer size={11} /> SPEED</div>
                    <div className="mt-1.5 text-xs font-medium text-neutral-200">{selected.eta}</div>
                  </div>
                </div>
                <p className="flex gap-3 text-xs leading-relaxed text-neutral-500">
                  <Play size={13} className="mt-0.5 shrink-0 text-gold/70" />
                  {selected.start_info}
                </p>
              </div>
            ) : (
              <p className="mt-5 flex gap-3 text-sm text-neutral-500">
                <Info size={15} className="mt-0.5 shrink-0" /> Select a service to see its description, rates and limits.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/[0.08] to-transparent p-6 sm:p-8" data-testid="neworder-summary">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">Estimated charge</span>
              <span className="text-3xl font-bold tracking-tight text-neutral-50" data-testid="neworder-charge">
                {inr(charge)}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
              <Wallet size={13} className="text-gold/70" />
              Balance: <span className="font-semibold text-neutral-300">{inr(user?.balance ?? 0)}</span>
            </div>

            {insufficient && (
              <p className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-xs text-amber-300" data-testid="neworder-balance-warning">
                Insufficient balance. <Link to="/panel/add-funds" className="font-semibold underline underline-offset-2">Add funds</Link> to place this order.
              </p>
            )}

            <button
              onClick={placeOrder}
              disabled={placing || !selected || !link.trim() || !qty || qty < (selected?.min ?? 0) || qty > (selected?.max ?? Infinity) || insufficient}
              data-testid="place-order-button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.15em] text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-all duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {placing && <Loader2 size={16} className="animate-spin" />}
              PLACE ORDER
            </button>
            <p className="mt-3 text-center font-mono text-[9px] tracking-[0.2em] text-neutral-600">
              CHARGED FROM WALLET · INSTANT CONFIRMATION
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
